import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
// Stylesheets — loaded in source order to preserve the cascade order of
// the original monolithic index.css. See V3/frontend/src/styles/README.md
// for the bucket map and the going-forward rule. Reordering these
// imports can silently change which rule wins; don't do it without
// verifying.
import './styles/tokens.css'
import './styles/base.css'
// Cross-component splash chrome (.splash, .splash-center, .splash-logo*,
// shared keyframes). Component-internal splash styles live in
// V3/frontend/src/components/SplashScreen.module.css and
// V3/frontend/src/components/QuestionBubbles.module.css per #264 PR A.
import './styles/splash-chrome.css'
// student-picker.css removed in #264 PR J (ADR 0036) — styles now in
// pages/StudentLogin.module.css (single consumer; the comment in the
// old file already noted "this section styles the post-login picker
// page at /student").
//
// site-gates.css removed in #264 PR H (ADR 0036) — distributed across
// 3 modules: components/SiteGate.module.css (.site-gate* + shake
// keyframe), components/TestingModeGate.module.css (just the .testingGate
// modifier; reaches into global .splash-logo-wrapper via :global()),
// and components/TestingPasswordPanel.module.css (.badge / .message /
// .pw* family; uses the global splash-fade-up keyframe by name).
//
// dev-quickstart.css removed in #264 PR I (ADR 0037) — styles now in
// pages/DevQuickstart.module.css (single consumer). The `.chip-dev`
// (DEV pill) and `.chip-outline` (small "Optional" variant) classes
// were kept local to that module since DevQuickstart was the only
// consumer. With its last raw consumer migrated, the global
// `.chip-outline` rule + dark-theme override dropped from
// dashboard-chrome.css too.
// The auth screens are styled by `components/AuthLayout.module.css`
// (ADR 0037); no global `.auth-*` selector remains.
//
// admin.css removed in #264 PR G — styles now live in 6 co-located
// modules (AdminTopbar / AdminHero / AdminKpiStrip /
// AdminClassListPanel / AdminPendingRail / AdminDashboard) + the
// admin-chrome.css below for the admin section, rail, field and modal rules
// shared between AdminClassListPanel + AdminPendingRail.
import './styles/admin-chrome.css'
import './styles/themes.css'
// App-shell chrome (.app-shell, .app-sidebar, .brand*, .nav-*, .topbar,
// .input-wrap) shared by the teacher and admin dashboards (ADR 0035).
import './styles/app-shell-chrome.css'
// View Transitions — Layer 1 of the UI-motion work (browser-native
// page transitions). CSS-only: styles the ``::view-transition-*``
// pseudo-elements + names ``.app-sidebar`` so it holds across
// intra-dashboard navigation. Loaded after app-shell-chrome.css so the
// ``.app-sidebar { view-transition-name }`` rule is additive on top of
// the sidebar's layout rules. Inert on routes that don't navigate with
// the router's ``viewTransition`` prop (e.g. the landing page).
import './styles/view-transitions.css'
// Residual dashboard chrome stub. After PR G:
//   - cross-cutting utilities (h-*, eyebrow, row, col, stack-*, mono,
//     tnum, divider, vrule) — permanent globals
//   - .card / .card-head / .section-head (StudentsSection + others)
//   - .seg segmented control (used by sub-section components)
//   - .chip + variants (raw-className consumers in StudentLogin → PR J,
//     TutoringPhase → leftover after PR E, DevQuickstart → PR I)
//   - .status-{ready,building,failed,pending} (SessionTranscripts via
//     status-${session.status})
//   - .assess + .drawer (StudentAssessments)
//   - .eehae-mark family (TutoringSession; PR E left the brand-mark CSS
//     in place — future PR)
//   - .paper-decor blobs (app-wide background — permanent global)
//   - Dark-theme overrides for everything still listed
// The stub continues to shrink as PRs H / I / J land + the EehaeMark
// follow-up runs.
import './styles/dashboard-chrome.css'
import './styles/session-chrome.css'
// ADR 0030 Phase 6 — full-page 정리노트 reading surface (layout C).
import './styles/notes.css'
// Cross-component glass-pill chrome (.glass-pill, .glass-pill--on-dark).
// LanguageSwitcher + ThemeSwitcher both compose it; themes.css writes
// 4 [data-theme="..."] overrides keyed on the unhashed name. The
// component-internal styles for the privacy surface live in:
//   - pages/PrivacyPolicy.module.css   (the .privacy-* page rules)
//   - components/CookieNotice.module.css
//   - components/PrivacyFooter.module.css
//   - components/LanguageSwitcher.module.css   (just positioning)
//   - components/ThemeSwitcher.module.css      (just positioning)
// per #264 PR C.
import './styles/glass-pill.css'
// marketing.css fully migrated to ``components/MarketingExtension.module.css``
// in #264 PR B; the snap behavior + section-scroll keys on the
// ``data-marketing-section`` attribute now (see snap.css below).
import './styles/snap.css'
// PIPA P3 (#129) PR 5 — account-settings page + confirmation modal.
// Loaded after dashboard.css so the ``.card`` overrides + the
// ``.modal-*`` rules land late in the cascade, which is what the
// page's higher-z-index dialog needs.
import './styles/account-settings.css'
// Help / documentation chrome (issue #620). Loaded late in the cascade
// so its `.help-*` rules + the `.callout`, `.inline-code`, `.code-block`
// markdown-targeted classes resolve after all earlier resets and don't
// collide with dashboard `.card` rules. Routes mounted in App.tsx
// under /teacher/help, /student/help, /admin/help.
import './styles/help-chrome.css'
import App from './App.tsx'
import LocalizedClerkProvider from './components/LocalizedClerkProvider'
import { LangProvider } from './i18n'
import { initSentry } from './sentry'

// Initialize Sentry as early as possible so module-load-time errors
// also surface. Gated on VITE_SENTRY_DSN; no-op otherwise. PII scrubber
// + tier-tag config lives in src/sentry.ts.
//
// Fire-and-forget: we don't await this. The init does a dynamic import
// of @sentry/react which would otherwise block first paint by ~50-100ms,
// and any errors in the brief window before init lands fall through to
// console.error (same as before this file existed).
void initSentry()

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!PUBLISHABLE_KEY) {
  throw new Error(
    'Missing VITE_CLERK_PUBLISHABLE_KEY in V3/frontend/.env.local',
  )
}

// Provider order matters:
//   • BrowserRouter first, so LangProvider can call useLocation()
//   • LangProvider second, so LocalizedClerkProvider can read the
//     active lang via useLang() and pass `localization` to Clerk
//   • LocalizedClerkProvider third, wrapping the real app
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <LangProvider>
        <LocalizedClerkProvider publishableKey={PUBLISHABLE_KEY}>
          <App />
        </LocalizedClerkProvider>
      </LangProvider>
    </BrowserRouter>
  </StrictMode>,
)
